import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Star } from 'lucide-react';
import { cn } from '@/lib/utils';
import { csatApi } from '@/lib/api';
import { toast } from '@/components/ui/sonner';

interface CsatRatingModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderNumber: string;
}

export const CsatRatingModal: React.FC<CsatRatingModalProps> = ( {
  isOpen,
  onClose,
  orderNumber,
} ) => {
  const [ rating, setRating ] = useState<number>( 0 );
  const [ hoveredRating, setHoveredRating ] = useState<number>( 0 );
  const [ commentOverall, setCommentOverall ] = useState( '' );
  const [ commentImprovement, setCommentImprovement ] = useState( '' );
  const [ isSubmitting, setIsSubmitting ] = useState( false );

  // Reset state when modal opens
  useEffect( () => {
    if ( isOpen ) {
      setRating( 0 );
      setHoveredRating( 0 );
      setCommentOverall( '' );
      setCommentImprovement( '' );
      setIsSubmitting( false );
    }
  }, [ isOpen ] );

  const isLowRating = rating >= 1 && rating <= 3;
  const isHighRating = rating >= 4 && rating <= 5;

  const isCommentImprovementRequired = isLowRating;

  const handleSubmit = async () => {
    if ( rating === 0 ) return;

    if ( isCommentImprovementRequired && !commentImprovement.trim() ) {
      toast.error( "Por favor, preencha o campo de melhoria." );
      return;
    }

    setIsSubmitting( true );
    try {
      await csatApi.submitRating( orderNumber, {
        feedbackProvided: true,
        rating,
        commentImprovement: isLowRating ? ( commentImprovement.trim() || null ) : null,
        commentOverall: commentOverall.trim() || null,
      } );
      toast.success( "Obrigado pela sua avaliação!" );
      onClose();
    } catch ( error: unknown ) {
      const axiosError = error as { response?: { status: number } };
      if ( axiosError.response?.status === 409 ) {
        toast.error( "Este pedido já foi avaliado." );
        onClose();
      } else {
        toast.error( "Erro ao enviar avaliação. Tente novamente mais tarde." );
      }
    } finally {
      setIsSubmitting( false );
    }
  };

  const handleRefuse = async () => {
    setIsSubmitting( true );
    try {
      await csatApi.submitRating( orderNumber, {
        feedbackProvided: false,
      } );
    } catch ( error ) {
      console.error( "Erro ao registrar recusa de CSAT:", error );
    } finally {
      setIsSubmitting( false );
      onClose();
    }
  };

  const handleModalClose = ( open: boolean ) => {
    if ( !open && !isSubmitting ) {
      handleRefuse();
    }
  };

  const handleKeyDown = ( e: React.KeyboardEvent, value: number ) => {
    let nextValue = 0;
    if ( e.key === 'ArrowRight' || e.key === 'ArrowDown' ) {
      nextValue = value < 5 ? value + 1 : 1;
    } else if ( e.key === 'ArrowLeft' || e.key === 'ArrowUp' ) {
      nextValue = value > 1 ? value - 1 : 5;
    }

    if ( nextValue ) {
      e.preventDefault();
      setRating( nextValue );
      const nextButton = document.querySelector( `button[role="radio"][aria-posinset="${ nextValue }"]` ) as HTMLButtonElement;
      nextButton?.focus();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleModalClose}>
      <DialogContent
        className="sm:max-w-1xl"
        onPointerDownOutside={( e ) => e.preventDefault()}
        onEscapeKeyDown={( e ) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-center text-xl mt-8">
            Como você avalia sua experiência <br /> de compra até agora?
          </DialogTitle>
          <DialogDescription className="sr-only">
            Este formulário permite avaliar sua satisfação. Utilize as setas do teclado para navegar entre as estrelas de 1 a 5 e a tecla Tab para navegar entre os campos de texto.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center space-y-6 py-4">
          <div
            role="radiogroup"
            aria-label="Avaliação de 1 a 5 estrelas"
            className="flex space-x-3"
          >
            {[ 1, 2, 3, 4, 5 ].map( ( starValue ) => (
              <button
                key={starValue}
                type="button"
                role="radio"
                aria-checked={rating === starValue}
                aria-posinset={starValue}
                aria-setsize={5}
                tabIndex={rating === starValue || ( rating === 0 && starValue === 1 ) ? 0 : -1}
                className={cn(
                  "focus:outline-none transition-all hover:scale-110 rounded-md p-2",
                  "focus-visible:ring-offset-4 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:outline-none",
                  rating === starValue && "ring-1 ring-yellow-400 ring-offset-2"
                )}
                onClick={() => setRating( starValue )}
                onMouseEnter={() => setHoveredRating( starValue )}
                onMouseLeave={() => setHoveredRating( 0 )}
                onKeyDown={( e ) => handleKeyDown( e, starValue )}
                aria-label={`${ starValue } ${ starValue === 1 ? 'estrela' : 'estrelas' }`}
              >
                <Star
                  className={cn(
                    "h-10 w-10 transition-colors",
                    ( hoveredRating || rating ) >= starValue
                      ? "fill-yellow-500 text-yellow-600" // Slightly darker for better contrast on white
                      : "text-muted-foreground"
                  )}
                  aria-hidden="true"
                />
              </button>
            ) )}
          </div>

          <div className="w-full space-y-5 animate-in fade-in slide-in-from-top-2">
            {isLowRating && (
              <div className="space-y-2">
                <Label htmlFor="csat-improvement" className="text-base font-semibold flex items-center">
                  Teve algum problema durante a sua compra?
                  <span className="ml-1 text-destructive" aria-hidden="true">*</span>
                </Label>
                <Textarea
                  id="csat-improvement"
                  placeholder="Conte-nos o que podemos fazer melhor..."
                  value={commentImprovement}
                  onChange={( e ) => setCommentImprovement( e.target.value )}
                  className="min-h-[100px] text-base resize-none focus-visible:ring-blue-600 focus-visible:ring-2"
                  aria-required="true"
                  aria-invalid={isCommentImprovementRequired && !commentImprovement.trim() && isSubmitting}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="csat-overall" className="text-base font-semibold">
                Deixe seu comentário ou sugestão <span className="text-muted-foreground font-normal text-sm">(opcional)</span>
              </Label>
              <Textarea
                id="csat-overall"
                placeholder="Escreva aqui seu comentário..."
                value={commentOverall}
                onChange={( e ) => setCommentOverall( e.target.value )}
                className="min-h-[100px] text-base resize-none focus-visible:ring-blue-600 focus-visible:ring-2"
              />
            </div>
          </div>
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-3 pt-4">
          {/*
          <Button
            variant="outline"
            onClick={handleRefuse}
            disabled={isSubmitting}
            className="sm:flex-1 order-2 sm:order-1 h-12 text-base border-2"
          >
            Agora não
          </Button>
        */}
          <Button
            onClick={handleSubmit}
            disabled={rating === 0 || isSubmitting || ( isCommentImprovementRequired && !commentImprovement.trim() )}
            className={cn(
              "sm:flex-1 order-1 sm:order-2 h-12 text-base font-bold transition-all",
              "bg-yellow-400 hover:bg-yellow-500 text-black border-2 border-yellow-600 shadow-sm hover:shadow-md",
              "focus-visible:ring-blue-600 focus-visible:ring-offset-2"
            )}
          >
            {isSubmitting ? "Enviando..." : "Enviar avaliação"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
